import React, { createContext, useContext, useState } from "react";
import { STORAGE_KEYS } from "../constants/userConstant";

export const PdfContext = createContext();

const PdfProvider = ({ children }) => {
  const [pdfGenerationStates, setPdfGenerationStates] = useState(() => {
    // Initialize from localStorage to persist across page refreshes
    const saved = localStorage.getItem("pdfGenerationStates");
    return saved ? JSON.parse(saved) : {};
  });

  const [pdfNotifications, setPdfNotifications] = useState(() => {
    // Initialize from localStorage to persist across page refreshes
    const saved = localStorage.getItem("pdfNotifications");
    return saved ? JSON.parse(saved) : {};
  });

  // Store pdfUrls in localStorage to persist across page refreshes
  const [pdfUrls, setPdfUrls] = useState(() => {
    const saved = localStorage.getItem("pdfUrls");
    return saved ? JSON.parse(saved) : {};
  });

  const setPdfGenerating = (bookId, isGenerating) => {
    setPdfGenerationStates(prev => {
      const newStates = {
        ...prev,
        [bookId]: isGenerating
      };
      
      // If not generating, remove the bookId from the state to clean up
      if (!isGenerating) {
        delete newStates[bookId];
      }
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.PDF_GENERATION_STATES, JSON.stringify(newStates));
      
      return newStates;
    });
  };

  const isPdfGenerating = (bookId) => {
    return pdfGenerationStates[bookId] || false;
  };

  const clearAllPdfStates = () => {
    setPdfGenerationStates({});
    localStorage.removeItem("pdfGenerationStates");
  };

  const setPdfNotification = (bookId, notificationData) => {
    // Separate pdfUrl from notification data
    const { pdfUrl, ...notificationWithoutUrl } = notificationData;
    
    // Store pdfUrl in localStorage
    if (pdfUrl) {
      setPdfUrls(prev => {
        const newUrls = {
          ...prev,
          [bookId]: pdfUrl
        };
        // Save to localStorage
        localStorage.setItem(STORAGE_KEYS.PDF_URLS, JSON.stringify(newUrls));
        return newUrls;
      });
    }
    
    setPdfNotifications(prev => {
      const newNotifications = {
        ...prev,
        [bookId]: notificationWithoutUrl
      };
      
      // Save to localStorage (without pdfUrl)
      localStorage.setItem(STORAGE_KEYS.PDF_NOTIFICATIONS, JSON.stringify(newNotifications));
      
      return newNotifications;
    });
  };

  const getPdfNotification = (bookId) => {
    return pdfNotifications[bookId] || null;
  };

  const clearPdfNotification = (bookId) => {
    setPdfNotifications(prev => {
      const newNotifications = { ...prev };
      delete newNotifications[bookId];
      
      // Update localStorage
      localStorage.setItem(STORAGE_KEYS.PDF_NOTIFICATIONS, JSON.stringify(newNotifications));
      
      return newNotifications;
    });
    
    // Also clear pdfUrl from localStorage
    setPdfUrls(prev => {
      const newUrls = { ...prev };
      delete newUrls[bookId];
      // Update localStorage
      localStorage.setItem(STORAGE_KEYS.PDF_URLS, JSON.stringify(newUrls));
      return newUrls;
    });
  };

  const getAllPdfNotifications = () => {
    return pdfNotifications;
  };

  const getPdfUrl = (bookId) => {
    return pdfUrls[bookId] || null;
  };

  // Refresh a stored pdfUrl in place (e.g. after re-fetching an expired signed URL)
  // without touching the notification banner.
  const updatePdfUrl = (bookId, pdfUrl) => {
    setPdfUrls(prev => {
      const newUrls = {
        ...prev,
        [bookId]: pdfUrl
      };
      localStorage.setItem(STORAGE_KEYS.PDF_URLS, JSON.stringify(newUrls));
      return newUrls;
    });
  };

  return (
    <PdfContext.Provider value={{
      setPdfGenerating,
      isPdfGenerating,
      clearAllPdfStates,
      setPdfNotification,
      getPdfNotification,
      clearPdfNotification,
      getAllPdfNotifications,
      getPdfUrl,
      updatePdfUrl
    }}>
      {children}
    </PdfContext.Provider>
  );
};

export const usePdf = () => useContext(PdfContext);

export default PdfProvider;